/**
 * Retrouve le monstre d'une zone d'enchère alors que son nom peut différer de la liste des zones :
 * lot renommé, zones regroupées (« Port de Madrestam / Larves / Côte d'Asse »), accents ou
 * majuscules différents, zone renommée après le lancement de l'enchère.
 */
const normalize = (name) => String(name || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/œ/g, 'oe')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const STOPWORDS = new Set(['de', 'des', 'du', 'la', 'le', 'les', 'et', 'en', 'au', 'aux', 'ile', 'zone']);
const tokens = (name) => normalize(name).split(' ').filter(w => w.length >= 3 && !STOPWORDS.has(w));
const wordMatch = (a, b) => a === b || (Math.min(a.length, b.length) >= 4 && (a.startsWith(b) || b.startsWith(a)));

// Part des mots retrouvés des deux côtés (0 à 1)
const similarity = (a, b) => {
    const ta = tokens(a);
    const tb = tokens(b);
    if (!ta.length || !tb.length) return 0;
    const inB = ta.filter(w => tb.some(x => wordMatch(w, x))).length / ta.length;
    const inA = tb.filter(x => ta.some(w => wordMatch(w, x))).length / tb.length;
    return Math.min(inA, inB) * 0.5 + Math.max(inA, inB) * 0.5;
};

const MIN_SIMILARITY = 0.75;

// Index des monstres (avec image) par nom normalisé, gardé tant que l'objet `monsters` ne change pas
const indexes = new WeakMap();
const indexOf = (monsters) => {
    let index = indexes.get(monsters);
    if (!index) {
        index = new Map();
        Object.entries(monsters).forEach(([zone, m]) => {
            if (!m?.img) return;
            index.set(normalize(zone), m);
            // Aussi sans la précision entre parenthèses : « Port de Madrestam (Amakna) » → « port de madrestam »
            const short = normalize(zone.replace(/\(.*?\)/g, ' '));
            if (short && !index.has(short)) index.set(short, m);
        });
        indexes.set(monsters, index);
    }
    return index;
};

/** Monstre à afficher pour une zone (nom du lot et, en secours, son donjon). undefined si rien ne correspond. */
export const findZoneMonster = (monsters, zone, donjon = '') => {
    if (!monsters) return undefined;
    if (monsters[zone]?.img) return monsters[zone];
    const index = indexOf(monsters);
    if (index.size === 0) return undefined;

    // Noms à essayer : le lot entier, chacune des zones regroupées, puis le donjon
    const parts = String(zone || '').split(/\s*[/+,]\s*|\s+-\s+/).filter(Boolean);
    const candidates = [zone, ...parts, donjon].filter(Boolean);

    for (const name of candidates) {
        const hit = index.get(normalize(name)) || index.get(normalize(name.replace(/\(.*?\)/g, ' ')));
        if (hit) return hit;
    }

    let best = null;
    for (const name of candidates) {
        for (const [key, m] of index) {
            const score = similarity(name, key);
            if (score >= MIN_SIMILARITY && (!best || score > best.score)) best = { m, score };
        }
    }
    return best?.m;
};
